import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StopSessionInput } from '@gc/shared';

import { BulkConfirmDialog } from '@/features/devices/BulkConfirmDialog';
import { runPerDevice, type BulkOutcome } from '@/features/devices/bulkActions';
import {
  STOP_ROUNDS_DEFAULT,
  StopRoundsLines,
  stopRoundsPayload,
  type StopRoundsEntry,
} from '@/features/devices/StopRoundsFields';
import { api } from '@/lib/api';
import type { RunningSelectedSession } from './selection';

/**
 * The stop body for one sitting ended from Outstanding: a round-priced
 * device's rounds as asked, an hourly one's `{}` — the Device List's End to
 * the letter (`EndReservationDialog`).
 */
export function outstandingStopBody(
  session: Pick<RunningSelectedSession, 'reservationType' | 'threeBallPriceMd'>,
  entry: StopRoundsEntry,
): StopSessionInput {
  return session.reservationType === 'ROUND'
    ? stopRoundsPayload(entry, session.threeBallPriceMd != null)
    : {};
}

/**
 * Outstanding's End — a row's and the selection's alike. It stops the clock
 * and settles nothing: the device becomes free while the money stays owed.
 * Sequential, one outcome per device, never aborting on the first failure:
 * the contract the device board's bulk actions use.
 *
 * **A round-priced device is asked for its rounds** (owner ruling OW19,
 * 2026-11-03). End used to post `{}` for every sitting; the stop schema reads
 * that as zero rounds, and a pool table or a console sold by the match is
 * priced on rounds alone, so every one ended here closed owing nothing for
 * play. Each round-priced line now carries the Device List's own Round Type
 * and Rounds, one round by default, 3 ball only where the sitting has a
 * 3-ball price. Hourly lines have nothing to count and are asked nothing.
 * The cursor opens in the first line's Rounds, as the Device List's End
 * opens it; with no round-priced line, in the typed count as before.
 *
 * Mounted fresh each time End opens, so a cancelled answer is never the next
 * End's default.
 */
export function OutstandingEndDialog({
  sessions,
  onClose,
  onFinished,
}: {
  /** The running sitting(s) being ended, live from the selection. */
  sessions: readonly RunningSelectedSession[];
  onClose: () => void;
  onFinished: () => void;
}) {
  const { t } = useTranslation();
  const [rounds, setRounds] = useState<ReadonlyMap<string, StopRoundsEntry>>(new Map());
  const entryFor = (sessionId: string) => rounds.get(sessionId) ?? STOP_ROUNDS_DEFAULT;
  const roundPriced = sessions.filter((session) => session.reservationType === 'ROUND');

  const run = (): Promise<BulkOutcome[]> =>
    runPerDevice(
      sessions.map((session) => ({ id: session.sessionId, name: session.deviceName, session })),
      ({ session }) =>
        api.post(
          `/sessions/${session.sessionId}/stop`,
          outstandingStopBody(session, entryFor(session.sessionId)),
        ),
    );

  return (
    <BulkConfirmDialog
      title={t('outstanding.endDialog.title')}
      message={t('outstanding.endDialog.message', { count: sessions.length })}
      actionLabel={t('outstanding.end')}
      deviceNames={sessions.map((session) => session.deviceName)}
      autoFocusTypedCount={roundPriced.length === 0}
      run={run}
      onClose={onClose}
      onFinished={onFinished}
    >
      <StopRoundsLines
        lines={roundPriced.map((session) => ({
          sessionId: session.sessionId,
          deviceName: session.deviceName,
          offersThreeBall: session.threeBallPriceMd != null,
        }))}
        rounds={rounds}
        onChange={(sessionId, next) =>
          setRounds((current) => new Map(current).set(sessionId, next))
        }
      />
    </BulkConfirmDialog>
  );
}
